import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { getFriends } from "../../../functions/user";
import { Search } from "../../../svg";
import Skeleton from "react-loading-skeleton";
import "./style.css";

const isUserOnline = (userId, onlineUsers) => {
  if (!userId || !onlineUsers) return false;
  const id = String(userId);
  if (onlineUsers instanceof Set) {
    return Array.from(onlineUsers).some((onlineId) => String(onlineId) === id);
  }
  if (Array.isArray(onlineUsers)) {
    return onlineUsers.some((onlineId) => String(onlineId) === id);
  }
  return false;
};

export default function NewConversationModal({
  currentUser,
  onlineUsers,
  onStartConversation,
  onClose,
}) {
  const [friends, setFriends] = useState([]);
  const [filteredFriends, setFilteredFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const { user } = useSelector((state) => ({ ...state }));

  const loadFriends = useCallback(async () => {
    try {
      setLoading(true);
      const friendsList = await getFriends(user.token);

      if (Array.isArray(friendsList)) {
        setFriends(friendsList);
        setFilteredFriends(friendsList);
      } else {
        console.error("Error loading friends:", friendsList);
        setFriends([]);
        setFilteredFriends([]);
      }
    } catch (error) {
      console.error("Error loading friends:", error);
      setFriends([]);
      setFilteredFriends([]);
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    loadFriends();
  }, [loadFriends]);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = friends.filter((friend) => {
        const fullName = `${friend.first_name} ${friend.last_name}`.toLowerCase();
        const username = (friend.username || "").toLowerCase();
        return fullName.includes(query) || username.includes(query);
      });
      setFilteredFriends(filtered);
    } else {
      setFilteredFriends(friends);
    }
  }, [searchQuery, friends]);

  const handleStartConversation = (friendId) => {
    onStartConversation(friendId);
  };

  return (
    <div className="modal_overlay" onClick={onClose}>
      <div
        className="new_conversation_modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-conversation-title"
      >
        <div className="ncm_header">
          <h3 id="new-conversation-title">New Conversation</h3>
          <button
            type="button"
            className="ncm_close_btn"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="exit_icon"></i>
          </button>
        </div>

        <div className="ncm_search">
          <div
            className={`ncm_search_bar${searchFocused ? " is_focused" : ""}`}
          >
            <span className="ncm_search_icon" aria-hidden="true">
              <Search color="#9CA3AF" />
            </span>
            <input
              type="text"
              placeholder="Search friends..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              className="ncm_search_input"
            />
          </div>
        </div>

        <div className="ncm_content">
          {loading ? (
            <div className="ncm_friends_list">
              {Array(8)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="ncm_friend_item ncm_friend_skeleton">
                    <Skeleton circle height={46} width={46} />
                    <div className="ncm_friend_info">
                      <Skeleton height={14} width="55%" />
                      <Skeleton height={12} width="35%" />
                    </div>
                  </div>
                ))}
            </div>
          ) : filteredFriends.length === 0 ? (
            <div className="ncm_empty">
              {searchQuery ? (
                <p>No friends found matching &quot;{searchQuery}&quot;</p>
              ) : (
                <>
                  <p>No friends to start a conversation with</p>
                  <p className="ncm_empty_hint">
                    Add some friends first to start chatting!
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="ncm_friends_list" role="list">
              {filteredFriends.map((friend) => {
                const online = isUserOnline(friend._id, onlineUsers);
                return (
                  <button
                    key={friend._id}
                    type="button"
                    className="ncm_friend_item"
                    onClick={() => handleStartConversation(friend._id)}
                    role="listitem"
                  >
                    <div className="ncm_avatar_wrap">
                      <img
                        src={friend.picture || "/images/default_profile.png"}
                        alt=""
                        className="ncm_friend_picture"
                      />
                      {online && (
                        <span
                          className="ncm_online_indicator"
                          aria-label="Online"
                        />
                      )}
                    </div>
                    <div className="ncm_friend_info">
                      <span className="ncm_friend_name">
                        {friend.first_name} {friend.last_name}
                      </span>
                      <span className="ncm_friend_username">
                        @{friend.username}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
